import { useEffect, useMemo, useState } from 'react';
import { memberApi } from '@formosa/shared/api/member';
import SettingsSubPageShell from '../../components/settings/SettingsSubPageShell';
import { useTranslation } from '../../i18n/LocaleContext';

export default function InvitePage() {
  const { t } = useTranslation();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const info = await memberApi.getInfo();
        if (!cancelled) {
          setMember(info || null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || t('invite.loadFailed'));
          setMember(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  const promotionCode = member?.promotionCode || '';

  const inviteLink = useMemo(() => {
    if (!promotionCode) {
      return '';
    }
    const url = new URL('/register', window.location.origin);
    url.searchParams.set('inviter', promotionCode);
    return url.toString();
  }, [promotionCode]);

  async function copyText(text) {
    if (!text) {
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setToast(t('invite.copied'));
      window.setTimeout(() => setToast(''), 2000);
    } catch {
      setToast(t('invite.copyFailed'));
      window.setTimeout(() => setToast(''), 2000);
    }
  }

  return (
    <SettingsSubPageShell title={t('invite.title')}>
      <div className="invite-page">
        {loading ? <p className="invite-page__status">{t('common.loading')}</p> : null}
        {error ? <p className="invite-page__status invite-page__status--error">{error}</p> : null}

        {!loading && !error ? (
          <>
            <p className="invite-page__hint">{t('invite.hint')}</p>

            <div className="settings-card invite-page__card">
              <div className="invite-page__row">
                <span className="invite-page__label">{t('invite.codeLabel')}</span>
                <span className="invite-page__value">{promotionCode || t('common.dash')}</span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={!promotionCode}
                  onClick={() => copyText(promotionCode)}
                >
                  {t('invite.copyCode')}
                </button>
              </div>

              <div className="invite-page__row invite-page__row--link">
                <span className="invite-page__label">{t('invite.linkLabel')}</span>
                <span className="invite-page__value invite-page__link">{inviteLink || t('common.dash')}</span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  disabled={!inviteLink}
                  onClick={() => copyText(inviteLink)}
                >
                  {t('invite.copyLink')}
                </button>
              </div>
            </div>
          </>
        ) : null}

        {toast ? <p className="invite-page__toast">{toast}</p> : null}
      </div>
    </SettingsSubPageShell>
  );
}
